// kfe-squiggle.js — squiggle pathway renderer (single page).
//
// PORT TARGET (Brief 198, CLAUDE.md Rule #9 — iOS is canon, mirror exactly):
//   ~/Developer/KFE-4.0-App @ origin/new_KFEframework (read-only)
//     iEnglish/Classes/CustomViews/KFEGatingLine/KFEGatingLineView.m
//     iEnglish/Classes/KFEModules/Main/StagesViewController.m   (orange / stage map)
//     iEnglish/Classes/KFEModules/Main/LessonsViewController.m  (blue / lesson map)
//
// Brief 002 ported Unity's `SquiggleRenderer.cs` grid instead of the iOS
// Bezier geometry, which left the web map a MIRROR IMAGE of the iPhone:
// it started bottom-LEFT, used 4 nodes on every row, had no state colours
// and an invented exit arrow. Brief 198 replaces that layout with the iOS
// one, node for node. Every constant below cites the iOS line it came from;
// see `IOS` and `REF` and `docs/198-handover.md`.
//
// Shape, in one paragraph
// ──────────────────────
// iOS draws one of two shapes per page, chosen on node count
// (`KFEGatingLineView.m:236` — `<14` → Z, else S):
//   · S-type (14…17 nodes) — five horizontal rows joined by half-circle
//     U-turns, a snake. Rows carry 3 nodes each except the first and last,
//     which carry 2/3/4 depending on the total (`:946-1012`).
//   · Z-type (3…13 nodes)  — a top row, a diagonal slash, a bottom row,
//     with the row/slash split taken from a lookup table (`:620-675`).
// In BOTH shapes the node numbers run BACKWARDS along the drawn path
// (`btn.index = count - arrayIndex`, `:589` / `:896`), so node 1 lands at
// the bottom-RIGHT and the highest number at the top-LEFT. The top-left
// node is also where the page exits: on a page with more to come
// (`isHaveMore`) iOS draws a dotted half-circle curling up and left into a
// solid right-pointing triangle (`:1165-1196`); on the LAST page it draws
// no exit at all and renders that same node as a five-pointed star
// (`:595-598` / `:904-907`, `:1537-1583`).
//
// Node state (Chris's ruling, 1 Nov 2026 — and exactly what iOS does)
// ───────────────────────────────────────────────────────────────────
// Both controllers run `redrawSquiggles` (gating off → every node
// NormalPlaying; gating on → inaccessible nodes Unavailable, the rest
// NormalPlaying) and THEN `updateSquigglesCompletedItemsToGreen`, which
// overwrites every `completed == 1` node with Passed. So the precedence is
// completed > locked > available, and the colours (`KFEGatingLineView.m:17-19`,
// applied to the number in `drawCircleLayer` `:1492-1510`) are:
//   · completed  → #8CC63F  kPassedTextColor
//   · available  → #FFFFFF  kPlayingTextColor      ("in process")
//   · locked     → #6D6D6D  kUnavailableTextColor  ("not yet accessible")
// The circle itself NEVER changes: #333333 fill, 1 pt white ring, always.
// Only the number (or the star) takes the state colour — iOS does not dim
// a locked node, so neither do we any more.
//
// The star takes the same state colour (`:1577-1578` — `stateColor`), which
// is why both reference screenshots show it green. `kEndingStarAllCompletedColor`
// (the tan RGB(176,138,97) Brief 002b adopted) is DEAD CODE in the shipped
// build: its only callers, `updateEndingStarBackgroundColor:` /
// `updateEndingStarState:`, are commented out in both controllers
// (`StagesViewController.m:188-189`, `LessonsViewController.m:208-209`).
//
// API
// ───
//   <kfe-squiggle .page=${pathwayPage}
//                 .level=${"stage" | "lesson"}
//                 .gatingEnabled=${true | false}
//                 on:select=${(e) => navigate(`/lesson/${e.detail.itemId}`)}
//                 on:locked=${(e) => toast(e.detail.message)}>
//   </kfe-squiggle>
//
// Tap on an Available or Completed node fires `select` with detail
// `{ itemId, displayNumber }`. Tap on a Locked node fires a `locked`
// event with detail `{ itemId, displayNumber, message }` AND surfaces a
// short live-region message inside the component itself so it works even
// if the parent doesn't subscribe (per brief 011 success criterion #2).
// When gating is off, every node is tappable and `locked` never fires.

import { KFEComponent } from "../core/component.js";
import { t } from "../core/i18n.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * iOS geometry constants. Units are iOS points, used 1:1 as SVG user units
 * (see `REF`). Every entry names the line it was read from in
 * `KFEGatingLineView.m` on `~/Developer/KFE-4.0-App @ origin/new_KFEframework`.
 * Brief 198 criterion 4: this object is the ONE place they live.
 */
export const IOS = {
  /** `:28` gatingLineStageBtnRadius — the drawn circle's outer radius. */
  nodeRadius: 18,
  /** `:29` gatingLineStagePlayingBtnRadius — also the top/bottom row inset. */
  playingNodeRadius: 23,
  /** `:23` gatingLineWidth — the squiggle stroke. */
  lineWidth: 4,
  /** `:1531` circleLayer.borderWidth (border colour is white, `:1532`). */
  ringWidth: 1,
  /** `:1529` circleLayer background gatingLineViewRGB(51,51,51). */
  nodeFill: "#333333",
  /** `:1434` titleLabel.font = boldSystemFontOfSize:20. */
  numberFontSize: 20,
  /** `:1539` fivePointStarRadius = self.radius - 5. */
  starInset: 5,
  /** `:1576` fivePointAngleLayer.lineWidth. */
  starStroke: 0.1,
  /** `:21` gatingLineSmallZRadius — Z corner radius for 6…13 nodes. */
  zRadiusSmall: 49.1,
  /** `:22` gatingLineBiggerZRadius — Z corner radius for ≤5 nodes. */
  zRadiusBig: 59.1,
  /** `:25` gatingLineBeginX — Z row inset for 6…13 nodes. */
  zBeginX: 20,
  /** `:26` gatingLineBeginBiggerX — Z row inset for ≤5 nodes. */
  zBeginXBig: 40,
  /** `:483` dotlineR — the Z-type exit curve's radius (the S-type uses its own R). */
  zExitRadius: 30,
  /** `:488` / `:858` triangleWidth_2 — half the exit arrowhead's height. */
  triangleHalf: 10,
  /** `:1170` the arrowhead's length, measured right from the curve's end. */
  triangleLength: 9,
  /** `:1178` triangleLayer.lineWidth (stroked AND filled in the line colour). */
  triangleStroke: 2.5,
  /** `:921` DxW — the 10 pt inset applied to 2- and 3-node S rows. */
  sRowInset: 10,
  /** `:1192` lineDashPattern (an 8 pt on/off dash) on the exit curve. */
  exitDash: 8,
  /** `:17` kPassedTextColor  RGB(140,198,63). */
  colorCompleted: "#8cc63f",
  /** `:18` kPlayingTextColor RGB(255,255,255). */
  colorAvailable: "#ffffff",
  /** `:19` kUnavailableTextColor RGB(109,109,109). */
  colorLocked: "#6d6d6d",
};

/**
 * The reference viewport the iOS geometry is pinned to.
 *
 * iOS sizes the squiggle from the DEVICE: `StagesViewController.m:451,457`
 * (and `LessonsViewController.m:399,403`) hand `KFEGatingLineView` a box of
 * `glv.frame.size.width - 70` × `glv.frame.size.height - 50`, where the glv
 * itself is the full screen width by `self.view.frame.size.height - 130`.
 * The corner radius R then falls out of that height
 * (`KFEGatingLineView.m:861-865`), so the whole shape is a function of the
 * available height.
 *
 * The web cannot read a device height at render time without measuring the
 * DOM, so we pin the box to the reference device the brief's screenshots came
 * from (`docs/198-reference/`, iPhone X-class, 375 × 812 pt, 1125 px at 3x) and
 * let the SVG viewBox scale it. The result is proportionally identical to the
 * iPhone at any rendered width. `boxH` is the one number measured rather than
 * read: 518 pt reproduces BOTH reference screenshots to the point —
 * R = (518 − 2·23)/8 = 59.0 on a last page and (518 − 23 − 10)/10 = 48.5 on a
 * page with more, which is exactly what the screenshots measure. See
 * `docs/198-handover.md` §"The one number we could not read from source".
 */
export const REF = {
  /** Reference screen width (the glv spans it edge to edge). */
  screenW: 375,
  /** `glv.frame.size.width - 70` — the drawing box's width. */
  boxW: 375 - 70,
  /** `glv.frame.size.height - 50` — the drawing box's height (see above). */
  boxH: 518,
  /** The glv (= viewBox) height: the drawing box plus the 50 pt it is inset by. */
  viewH: 518 + 50,
  /** The drawing box is centred in the glv (`KFEGatingLineView.m:436` / `:854`). */
  get offsetX() { return (this.screenW - this.boxW) / 2; },
  get offsetY() { return (this.viewH - this.boxH) / 2; },
};

/**
 * S-type row sizes, by total node count (`KFEGatingLineView.m:946-1012`,
 * read bottom row first since that is where node 1 sits). Rows 2-4 always
 * carry 3. Counts outside 14…17 never reach the S path (`:236` sends <14 to
 * the Z path; `:836-839` clamps >17 down to 16).
 *
 * @type {Record<number, {first: number, last: number}>}
 */
const S_ROWS = {
  14: { first: 2, last: 3 }, // 2+3+3+3+3
  15: { first: 3, last: 3 }, // 3+3+3+3+3
  16: { first: 3, last: 4 }, // 3+3+3+3+4  ← 16 stages per page, the common case
  17: { first: 4, last: 4 }, // 4+3+3+3+4
};

/**
 * Z-type row sizes [top, slash, bottom] by total node count — the lookup in
 * `KFEGatingLineView.m:620-675` (and its own comment at `:611-613`).
 *
 * Counts 1 and 2 are a WEB-ONLY addition: iOS leaves all three counters at 0
 * for them, so `caculatePositonsWithStageButtonsCount` returns an empty array
 * and the page draws no nodes at all. That is an iOS bug, not canon; a page
 * of 1-2 nodes would render blank on the web too. We put them on the bottom
 * row, where node 1 lives in every other count. No Zero-to-Fluency stage hits
 * this (the smallest is 3 lessons) — see `docs/198-handover.md`.
 *
 * @type {Record<number, [number, number, number]>}
 */
const Z_ROWS = {
  1: [0, 0, 1],  // web-only guard, see above
  2: [0, 0, 2],  // web-only guard, see above
  3: [1, 1, 1],
  4: [1, 2, 1],
  5: [1, 3, 1],
  6: [2, 2, 2],
  7: [2, 3, 2],
  8: [2, 4, 2],
  9: [3, 3, 3],
  10: [3, 4, 3],
  11: [3, 5, 3],
  12: [4, 4, 4],
  13: [4, 5, 4],
};

/** `KFEGatingLineView.m:236` — the Z/S switch. */
const S_TYPE_MIN_NODES = 14;

/**
 * Five-pointed star polygon, centred on (0,0), mirroring the construction in
 * `KFEGatingLineView.m:1544-1568`: outer points at 90° + 72°i, inner points at
 * 54° + 72°i on a circle of radius `r0 = R·sin18° / cos36°`, y measured upward.
 *
 * @param {number} radius outer radius (iOS: node radius − 5)
 */
function starPoints(radius) {
  const th = Math.PI / 180;
  const r0 = (radius * Math.sin(18 * th)) / Math.cos(36 * th);
  /** @param {number} r @param {number} deg */
  const pt = (r, deg) => `${(r * Math.cos(deg * th)).toFixed(2)},${(-r * Math.sin(deg * th)).toFixed(2)}`;
  const pts = [pt(radius, 90)];
  for (let i = 1; i < 5; i++) {
    pts.push(pt(r0, 54 + i * 72));
    pts.push(pt(radius, 90 + i * 72));
  }
  pts.push(pt(r0, 54));
  return pts.join(" ");
}

const STAR_POINTS = starPoints(IOS.nodeRadius - IOS.starInset);

export class KFESquiggle extends KFEComponent {
  static props = ["page", "level", "gatingEnabled"];
  static styles = `
    :host {
      display: block;
      position: relative;
      width: 100%;
      min-width: 280px;
      max-width: var(--kfe-app-max-width, 480px);
      margin-inline: auto;
    }
    svg { display: block; width: 100%; height: auto; overflow: visible; }

    /* Path stroke colour is set on the host via CSS custom property based
       on level (stage = orange, lesson = cyan). iOS line weight is
       gatingLineWidth = 4 pt with the default butt cap. */
    .line, .exit {
      fill: none;
      stroke: var(--kfe-line-stroke, var(--kfe-line-stage, #ff931e));
      stroke-width: ${IOS.lineWidth};
      stroke-linejoin: round;
    }
    /* KFEGatingLineView.m:1192 — lineDashPattern @[@8]. */
    .exit { stroke-dasharray: ${IOS.exitDash}; }
    /* KFEGatingLineView.m:1176-1181 — the arrowhead is both filled and
       stroked in the line colour, with a mitred tip. */
    .exit-head {
      fill: var(--kfe-line-stroke, var(--kfe-line-stage, #ff931e));
      stroke: var(--kfe-line-stroke, var(--kfe-line-stage, #ff931e));
      stroke-width: ${IOS.triangleStroke};
      stroke-linejoin: miter;
    }

    /* The circle itself is state-independent on iOS (KFEGatingLineView.m:1527-1533):
       #333 fill, 1 pt white ring, in every state. A locked node is NOT dimmed —
       only its number goes grey. Brief 198 removed the web's opacity dimming. */
    .node .fill { fill: var(--kfe-node-fill, ${IOS.nodeFill}); }
    .node .ring { fill: none; stroke: var(--kfe-node-border, #ffffff); stroke-width: ${IOS.ringWidth}; }
    .node text {
      font-family: var(--kfe-font);
      font-weight: 700;
      font-size: ${IOS.numberFontSize}px;
      text-anchor: middle;
      dominant-baseline: central;
      fill: var(--kfe-node-text, ${IOS.colorAvailable});
      pointer-events: none;
      user-select: none;
    }
    .node .star {
      fill: var(--kfe-node-text, ${IOS.colorAvailable});
      stroke: var(--kfe-node-text, ${IOS.colorAvailable});
      stroke-width: ${IOS.starStroke};
    }

    /* The three states, in iOS's own precedence order: redrawSquiggles paints
       locked/available, then updateSquigglesCompletedItemsToGreen overwrites
       completed. The .completed rule therefore comes last. */
    .node.locked text, .node.locked .star {
      fill: var(--kfe-node-locked, ${IOS.colorLocked});
      stroke: var(--kfe-node-locked, ${IOS.colorLocked});
    }
    .node.completed text, .node.completed .star {
      fill: var(--kfe-node-completed, ${IOS.colorCompleted});
      stroke: var(--kfe-node-completed, ${IOS.colorCompleted});
    }

    .node.tappable { cursor: pointer; }
    .hit { fill: transparent; }
    .node:focus-visible .ring { stroke-width: 3; outline: none; }

    /* Locked-tap toast — short, transient, screen-reader-friendly via
       aria-live="polite". Sits over the squiggle so it doesn't reflow the
       page. CSS-only fade so we don't ship a JS animation runner for it. */
    .locked-toast {
      position: absolute;
      left: 50%;
      bottom: 0.75rem;
      transform: translateX(-50%);
      padding: 0.5rem 0.9rem;
      border-radius: 999px;
      background: var(--kfe-toast-bg, rgba(20, 20, 20, 0.92));
      color: var(--kfe-toast-fg, #fff);
      font-size: 0.85rem;
      font-family: var(--kfe-font);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.18s ease-in-out;
      max-width: 80%;
      text-align: center;
      white-space: nowrap;
    }
    .locked-toast.shown { opacity: 1; }
  `;

  /** @type {ReturnType<typeof setTimeout> | null} */
  _toastTimer = null;

  /** @returns {DocumentFragment | undefined} */
  render() {
    const page = /** @type {import("../data/pathway-builder.js").PathwayPage | null | undefined} */ (
      /** @type {any} */ (this).page
    );
    if (!page || page.nodes.length === 0) return undefined;

    const level = /** @type {any} */ (this).level === "lesson" ? "lesson" : "stage";
    const gatingEnabled = /** @type {any} */ (this).gatingEnabled !== false;

    const stroke = level === "lesson"
      ? "var(--kfe-line-lesson, #29abe2)"
      : "var(--kfe-line-stage, #ff931e)";
    this.style.setProperty("--kfe-line-stroke", stroke);

    const states = page.nodes;
    const layout = computeLayout(states.length, page.hasNextPage === true);

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${REF.screenW} ${REF.viewH}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg.setAttribute("role", "list");
    svg.setAttribute("aria-label", level === "lesson" ? "Lesson pathway" : "Stage pathway");
    svg.dataset.shape = layout.shape;

    // The squiggle first, so nodes overlay it. The stroke is always at full
    // brightness — iOS never dims it (the whole path is bright even when only
    // node 1 is reachable; see the reference screenshots).
    const line = document.createElementNS(SVG_NS, "path");
    line.setAttribute("d", layout.linePath);
    line.setAttribute("class", "line");
    svg.appendChild(line);

    // On a page with more to come, the dotted curve + arrowhead; on the last
    // page, nothing here and a star on the highest-numbered node instead.
    if (layout.exit) {
      const curve = document.createElementNS(SVG_NS, "path");
      curve.setAttribute("d", layout.exit.curve);
      curve.setAttribute("class", "exit");
      svg.appendChild(curve);

      const head = document.createElementNS(SVG_NS, "polygon");
      head.setAttribute("points", layout.exit.head);
      head.setAttribute("class", "exit-head");
      svg.appendChild(head);
    }

    for (let i = 0; i < states.length; i++) {
      const state = states[i];
      const pos = layout.positions[i];
      if (!pos) continue;
      const displayNumber = page.startNumber + i;
      const isLocked = gatingEnabled && state.nodeState === "locked";
      // iOS canon: green is the COMPLETED cue (kPassedTextColor), not the
      // current/available one. A completed node is never locked, so the
      // gating-off case still renders its green correctly.
      const isCompleted = state.nodeState === "completed";
      const node = makeNode(pos, state, displayNumber, isLocked, isCompleted);
      node.addEventListener("click", () => {
        if (isLocked) {
          const message = t("pathway.locked");
          this._showLockedToast(message);
          this.dispatchEvent(new CustomEvent("locked", {
            detail: { itemId: state.itemId, displayNumber, message },
            bubbles: true,
            composed: true,
          }));
          return;
        }
        this.dispatchEvent(new CustomEvent("select", {
          detail: { itemId: state.itemId, displayNumber },
          bubbles: true,
          composed: true,
        }));
      });
      svg.appendChild(node);
    }

    const toast = document.createElement("div");
    toast.className = "locked-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.dataset.role = "locked-toast";

    const frag = document.createDocumentFragment();
    frag.appendChild(svg);
    frag.appendChild(toast);
    return frag;
  }

  /** @param {string} message */
  _showLockedToast(message) {
    const toast = /** @type {HTMLElement | null} */ (
      this.shadowRoot?.querySelector('[data-role="locked-toast"]')
    );
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add("shown");
    if (this._toastTimer != null) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.classList.remove("shown");
      this._toastTimer = null;
    }, 1800);
  }

  onDisconnected() {
    if (this._toastTimer != null) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
  }
}

/**
 * @typedef {{ x: number, y: number }} Point
 *
 * @typedef {object} Layout
 * @property {"s" | "z"} shape
 * @property {Point[]}   positions   index 0 = node 1 (bottom-right), ascending
 * @property {string}    linePath    the squiggle's `d`
 * @property {{curve: string, head: string} | null} exit  dotted next-page curve + arrowhead
 */

/**
 * Lay out one page exactly as iOS does.
 *
 * `KFEGatingLineView.m:236` picks the shape on node count; both shapes then
 * number their nodes BACKWARDS along the drawn path (`:589` / `:896`,
 * `btn.index = count - arrayIndex`), which is what puts node 1 at the
 * bottom-right. We build the path-order array exactly as iOS does and reverse
 * it, so `positions[0]` is node 1.
 *
 * @param {number} count
 * @param {boolean} hasMore `isHaveMore` — this page is not the last one
 * @returns {Layout}
 */
export function computeLayout(count, hasMore) {
  const local = count >= S_TYPE_MIN_NODES ? sTypeLayout(count, hasMore) : zTypeLayout(count, hasMore);
  const { offsetX, offsetY } = REF;
  /** @param {Point} p */
  const shift = (p) => ({ x: p.x + offsetX, y: p.y + offsetY });
  return {
    shape: local.shape,
    // Reverse: iOS builds positions in descending node order.
    positions: local.pathOrder.map(shift).reverse(),
    linePath: translatePath(local.linePath, offsetX, offsetY),
    exit: local.exit
      ? {
          curve: translatePath(local.exit.curve, offsetX, offsetY),
          head: translatePoints(local.exit.head, offsetX, offsetY),
        }
      : null,
  };
}

/**
 * S-type (snake): five rows joined by half-circle U-turns.
 * `KFEGatingLineView.m:850-1154`.
 *
 * @param {number} count
 * @param {boolean} hasMore
 */
function sTypeLayout(count, hasMore) {
  const { boxW, boxH } = REF;
  const PR = IOS.playingNodeRadius;
  const TH = IOS.triangleHalf;
  const DxW = IOS.sRowInset;

  // `:861-865`. A page with more to come gives up the top 2R + triangleHalf
  // of the box to the exit curve, so its R is smaller.
  const R = hasMore ? (boxH - PR - TH) / 10 : (boxH - 2 * PR) / 8;
  // `:919-920` DxR — the downward shift that makes room for the exit curve.
  const dxr = hasMore ? 2 * R + TH - PR : 0;
  /** Row k (0 = top … 4 = bottom), `:1089-1100`. */
  const rowY = (/** @type {number} */ k) => PR + 2 * k * R + dxr;

  const lineLeft = R;
  const lineRight = boxW - R; // `:1095` rightPointX = R + sLineWidth - 2R

  // `:925-944` — a 2- or 3-node row is inset by DxW from the curve ends;
  // a 4-node row sits exactly on them and divides the span in thirds.
  const inset2 = [lineLeft + DxW, lineRight - DxW];
  const inset3 = [lineLeft + DxW, boxW / 2, lineRight - DxW];
  const full4 = [
    lineLeft,
    lineLeft + (lineRight - lineLeft) / 3,
    lineLeft + (2 * (lineRight - lineLeft)) / 3,
    lineRight,
  ];
  const rows = S_ROWS[count] ?? S_ROWS[16];
  /** @param {number} n @returns {number[]} */
  const xs = (n) => (n === 2 ? inset2 : n === 4 ? full4 : inset3);

  /** @type {Point[]} */
  const pathOrder = [];
  const counts = [rows.first, 3, 3, 3, rows.last];
  for (let k = 0; k < 5; k++) {
    const y = rowY(k);
    // `:1014-1044` — the array runs left→right on even rows, right→left on odd.
    const row = xs(counts[k]).map((x) => ({ x, y }));
    pathOrder.push(...(k % 2 === 0 ? row : row.slice().reverse()));
  }

  // `:1102-1151` — five straight rows and four half-circle corners, right,
  // left, right, left. sweep=1 bulges right, sweep=0 bulges left.
  const seg = [`M ${f(lineLeft)} ${f(rowY(0))} L ${f(lineRight)} ${f(rowY(0))}`];
  for (let k = 1; k < 5; k++) {
    const onRight = k % 2 === 1;
    const x = onRight ? lineRight : lineLeft;
    seg.push(`A ${f(R)} ${f(R)} 0 0 ${onRight ? 1 : 0} ${f(x)} ${f(rowY(k))}`);
    seg.push(`L ${f(onRight ? lineLeft : lineRight)} ${f(rowY(k))}`);
  }

  return {
    shape: /** @type {"s"} */ ("s"),
    pathOrder,
    linePath: seg.join(" "),
    // `:1053-1056` — the S-type exit curve uses the row radius R.
    exit: hasMore ? exitArrow(R) : null,
  };
}

/**
 * Z-type: a top row, a diagonal slash, a bottom row.
 * `KFEGatingLineView.m:472-776`.
 *
 * The slash's angle comes from iOS's quadratic (`:453-471` derives it; the
 * code is `:506-513`): solve for the x-intercept offset dX that makes the
 * slash tangent to both corner arcs, then angleA = atan(H / (W + 2·dX)).
 *
 * @param {number} count
 * @param {boolean} hasMore
 */
function zTypeLayout(count, hasMore) {
  const { boxW: W, boxH } = REF;
  const PR = IOS.playingNodeRadius;

  // `:481-499`.
  let H, dY;
  if (hasMore) {
    const dotR = IOS.zExitRadius;
    H = boxH - PR - 10.0 - 2 * dotR;
    dY = 10.0 + 2 * dotR - PR;
  } else {
    H = boxH - 2 * PR;
    dY = 0;
  }

  // `:334-341` — a short page gets the bigger corner radius and a deeper inset.
  const beginX = count <= 5 ? IOS.zBeginXBig : IOS.zBeginX;
  let R = count <= 5 ? IOS.zRadiusBig : IOS.zRadiusSmall;
  if (R > H / 2) R = H / 2 - 1; // `:502-504`

  const A = H * H - 4 * H * R;
  const B = 2 * (H * R - W * R) * (H - 2 * R) - 4 * W * R * R;
  const C = (H * R - W * R) * (H * R - W * R) - R * R * H * H - R * R * W * W;
  const dX = (-B + Math.sqrt(B * B - 4 * A * C)) / (2 * A);
  const angleA = Math.atan(H / (W + 2 * dX));

  // `:517-548`. Everything then shifts down by playingStageButtonRadius — the
  // path via the shape layer's frame (`:558-560`), the nodes via
  // buttonsLevelKeyPoint (`:573-578`) — so we fold that into dY here.
  const y0 = dY + PR;
  const p1 = { x: beginX, y: y0 };
  const p2 = { x: W - R, y: y0 };
  const p3 = { x: W - R + R * Math.sin(angleA), y: R + R * Math.cos(angleA) + y0 };
  const p4 = { x: R - R * Math.sin(angleA), y: H - R - R * Math.cos(angleA) + y0 };
  const p5 = { x: R, y: H + y0 };
  const p6 = { x: W - beginX, y: H + y0 };

  const [topN, slashN, bottomN] = Z_ROWS[count] ?? Z_ROWS[13];

  /** @type {Point[]} */
  const pathOrder = [];
  // `:678-699` — the top row. One node sits at p1 (the left end).
  pathOrder.push(...spread(p1, p2, topN, true));
  // `:701-741` — the slash, p3 (top-right) → p4 (bottom-left). One node sits
  // at the midpoint, not at an end.
  if (slashN === 1) pathOrder.push(mid(p3, p4));
  else pathOrder.push(...spread(p3, p4, slashN, false));
  // `:743-767` — the bottom row. One node sits at p6 (the RIGHT end).
  if (bottomN === 1) pathOrder.push(p6);
  else pathOrder.push(...spread(p5, p6, bottomN, false));

  const linePath = [
    `M ${f(p1.x)} ${f(p1.y)} L ${f(p2.x)} ${f(p2.y)}`,
    `A ${f(R)} ${f(R)} 0 0 1 ${f(p3.x)} ${f(p3.y)}`,
    `L ${f(p4.x)} ${f(p4.y)}`,
    `A ${f(R)} ${f(R)} 0 0 0 ${f(p5.x)} ${f(p5.y)}`,
    `L ${f(p6.x)} ${f(p6.y)}`,
  ].join(" ");

  return {
    shape: /** @type {"z"} */ ("z"),
    pathOrder,
    linePath,
    // `:487-491` — the Z-type exit curve uses a fixed radius of 30, and is
    // NOT subject to the +playingNodeRadius shift (it is drawn straight onto
    // the view's layer); its foot still lands on the top row, at y = 2·30 + 10.
    exit: hasMore ? exitArrow(IOS.zExitRadius) : null,
  };
}

/**
 * The next-page exit: a dotted half-circle that leaves the top-left node,
 * curls up and to the LEFT, and ends in a solid right-pointing triangle.
 * `KFEGatingLineView.m:1165-1196`.
 *
 * @param {number} r the curve radius (S-type: the row radius; Z-type: 30)
 */
function exitArrow(r) {
  const th = IOS.triangleHalf;
  // `:1188-1189` — centre (r, r + 10), swept clockwise from the bottom of the
  // circle to the top, i.e. around its left side.
  const foot = { x: r, y: 2 * r + th };
  const tip = { x: r, y: th };
  // `:1167-1175` — an isosceles triangle whose base sits on the curve's end.
  const head = [
    `${f(tip.x)},${f(tip.y - (th - 2))}`,
    `${f(tip.x)},${f(tip.y + (th - 2))}`,
    `${f(tip.x + IOS.triangleLength)},${f(tip.y)}`,
  ].join(" ");
  return {
    curve: `M ${f(foot.x)} ${f(foot.y)} A ${f(r)} ${f(r)} 0 0 1 ${f(tip.x)} ${f(tip.y)}`,
    head,
  };
}

/**
 * `n` evenly spaced points from `a` to `b` inclusive. `flatY` keeps every
 * point on `a`'s y (iOS does this for the Z top row, where p2 shares p1's y
 * anyway — it is written out at `:688` as `keyPoint1.y`).
 *
 * @param {Point} a @param {Point} b @param {number} n @param {boolean} flatY
 * @returns {Point[]}
 */
function spread(a, b, n, flatY) {
  if (n <= 0) return [];
  if (n === 1) return [a];
  /** @type {Point[]} */
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    out.push({ x: a.x + (b.x - a.x) * t, y: flatY ? a.y : a.y + (b.y - a.y) * t });
  }
  return out;
}

/** @param {Point} a @param {Point} b @returns {Point} */
function mid(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Round to 2 dp — keeps the emitted `d` readable and diffable. @param {number} n */
function f(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Translate an SVG path built from absolute M/L/A commands. Cheap and exact
 * for the two shapes above; it is not a general path transformer.
 *
 * @param {string} d @param {number} dx @param {number} dy
 */
function translatePath(d, dx, dy) {
  const tokens = d.split(" ");
  const out = [];
  for (let i = 0; i < tokens.length; i++) {
    const cmd = tokens[i];
    if (cmd === "M" || cmd === "L") {
      out.push(cmd, f(Number(tokens[i + 1]) + dx), f(Number(tokens[i + 2]) + dy));
      i += 2;
    } else if (cmd === "A") {
      // rx ry rot large sweep x y — only the last pair moves.
      out.push(cmd, tokens[i + 1], tokens[i + 2], tokens[i + 3], tokens[i + 4], tokens[i + 5],
        f(Number(tokens[i + 6]) + dx), f(Number(tokens[i + 7]) + dy));
      i += 7;
    } else {
      out.push(cmd);
    }
  }
  return out.join(" ");
}

/** @param {string} points @param {number} dx @param {number} dy */
function translatePoints(points, dx, dy) {
  return points
    .split(" ")
    .map((p) => {
      const [x, y] = p.split(",").map(Number);
      return `${f(x + dx)},${f(y + dy)}`;
    })
    .join(" ");
}

/**
 * Build a single node group at `pos` — a #333 disc with a 1 pt white ring and,
 * on top, either the number or (for the last node of the last page) a star.
 * `KFEGatingLineView.m:1490-1583`. A transparent hit circle gives the touch
 * area a comfortable radius on phones; iOS gets the same for free because its
 * button frame is `playingStageButtonRadius` wide (`:601` / `:901`).
 *
 * @param {Point} pos
 * @param {import("../data/pathway-builder.js").ItemState} state
 * @param {number}  displayNumber
 * @param {boolean} isLocked
 * @param {boolean} isCompleted
 */
function makeNode(pos, state, displayNumber, isLocked, isCompleted) {
  const g = document.createElementNS(SVG_NS, "g");
  const classes = ["node"];
  if (isLocked) classes.push("locked");
  else classes.push("tappable");
  if (isCompleted) classes.push("completed");
  g.setAttribute("class", classes.join(" "));
  g.setAttribute("role", "button");
  g.setAttribute("tabindex", isLocked ? "-1" : "0");
  const ariaLabel = state.title
    ? `${displayNumber}. ${state.title} (${state.nodeState})`
    : `Item ${displayNumber} (${state.nodeState})`;
  g.setAttribute("aria-label", ariaLabel);
  g.setAttribute("aria-disabled", isLocked ? "true" : "false");

  const fill = document.createElementNS(SVG_NS, "circle");
  fill.setAttribute("cx", String(f(pos.x)));
  fill.setAttribute("cy", String(f(pos.y)));
  fill.setAttribute("r", String(IOS.nodeRadius));
  fill.setAttribute("class", "fill");
  g.appendChild(fill);

  // iOS draws the 1 pt border INSIDE the circle's bounds, so the ring's outer
  // edge is the node radius — hence the half-stroke inset here.
  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", String(f(pos.x)));
  ring.setAttribute("cy", String(f(pos.y)));
  ring.setAttribute("r", String(IOS.nodeRadius - IOS.ringWidth / 2));
  ring.setAttribute("class", "ring");
  g.appendChild(ring);

  if (state.isStarMilestone) {
    const star = document.createElementNS(SVG_NS, "polygon");
    star.setAttribute("points", STAR_POINTS);
    star.setAttribute("transform", `translate(${f(pos.x)} ${f(pos.y)})`);
    star.setAttribute("class", "star");
    g.appendChild(star);
  } else {
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", String(f(pos.x)));
    text.setAttribute("y", String(f(pos.y)));
    text.textContent = String(displayNumber);
    g.appendChild(text);
  }

  // Transparent hit area (always last so it's on top).
  const hit = document.createElementNS(SVG_NS, "circle");
  hit.setAttribute("cx", String(f(pos.x)));
  hit.setAttribute("cy", String(f(pos.y)));
  hit.setAttribute("r", String(IOS.playingNodeRadius));
  hit.setAttribute("class", "hit");
  g.appendChild(hit);

  return g;
}
